import Alpine from 'alpinejs'
import 'htmx.org';
import { realityCoinStore } from './realityCoinStore';
import { realityTokenStore } from './realityTokenStore';
import { realityCoins } from './realityCoins';
import moonPage from './moonPage';
import { treePage } from './treePage';
import { createWalletStore } from './walletStore';

import { joPage } from './joPage';
import { eggPage } from './eggPage';

window.Alpine = Alpine

// Start Alpine when the page is ready.
window.addEventListener('DOMContentLoaded', () => {
  Alpine.start()
});

// Initialize Alpine stores and global functions
window.addEventListener('alpine:initializing', () => {
  // Initialize translations store with default state
  Alpine.store('translations', {
    currentLang: localStorage.getItem('lang') || 'en',
    translations: {},
    t(key) {
      const keys = key.split('.');
      let value = this.translations[this.currentLang];
      for (const k of keys) {
        value = value?.[k];
      }
      return value || key;
    },
    async init() {
      const fallbackTranslations = {
        en: {
          nav: {
            home: 'Home',
            reality: 'Reality',
            games: 'Games',
            more: 'More',
            about: 'About',
            dashboard: 'Dashboard',
            getMoney: 'Get Money'
          },
          reality: {
            overview: 'Overview',
            tokens: 'Reality Tokens',
            coins: 'Reality Coins',
            tags: 'Reality Tags',
            bonds: 'Bonds',
            allBonds: 'All Bonds',
            loveBurn: 'Love Burn Bonds'
          }
        }
      };

      try {
        const response = await fetch('/translations.json', { cache: 'no-store' });
        if (!response.ok) {
          console.warn(`Translations unavailable (HTTP ${response.status}). Using fallback.`);
          this.translations = fallbackTranslations;
          return;
        }
        const contentType = response.headers.get('content-type') || '';
        const raw = await response.text();
        if (!contentType.includes('application/json')) {
          console.warn('Translations response was not JSON. Using fallback.');
          this.translations = fallbackTranslations;
          return;
        }

        let translations;
        try {
          translations = JSON.parse(raw);
        } catch (error) {
          console.warn('Translations JSON parse failed. Using fallback.');
          this.translations = fallbackTranslations;
          return;
        }
        
        // Validate translations structure
        if (!translations || typeof translations !== 'object') {
          console.warn('Invalid translations format. Using fallback.');
          this.translations = fallbackTranslations;
          return;
        }
        
        // Ensure default English translations exist
        if (!translations.en) {
          console.warn('Missing English translations, using fallback');
          translations.en = fallbackTranslations.en;
        }
        
        this.translations = translations;
      } catch (error) {
        console.warn('Failed to load translations. Using fallback.', error);
        this.translations = fallbackTranslations;
      }
    }
  });

  // Initialize translations
  Alpine.store('translations').init();

  Alpine.data('moonPage', moonPage);
  Alpine.data('joPage', joPage);
  Alpine.data('eggPage', eggPage);
  Alpine.data('treePage', treePage);
  
  // Add our new realityCoins component
  Alpine.data('realityCoins', realityCoins);

  // Navigation store (existing)
  Alpine.store('nav', {
    isOpen: false,
    close() { return this.isOpen = false },
    open() { return this.isOpen = true },
    toggle() { return this.isOpen = !this.isOpen }
  });

  const walletStore = createWalletStore();
  Alpine.store('wallet', walletStore);
  walletStore.tryAutoConnect();

  // Reality Coin store
  Alpine.store('realityCoins', realityCoinStore);

  // Reality Token store
  Alpine.store('realityToken', realityTokenStore);

  // Leaderboard store
  Alpine.store('leaderboard', {
    players: [
      {
        address: '0x1234567890abcdef',
        score: 1500,
        favoriteGame: 'CSEAS Spin'
      },
      {
        address: '0xabcdef1234567890',
        score: 1200,
        favoriteGame: 'WaveLens'
      },
      {
        address: '0x9876543210fedcba',
        score: 1000,
        favoriteGame: 'Bubble Bobber'
      }
    ]
  });

  // Tap store for NFC functionality
  Alpine.store('tap', {
    nfcStatus: 'idle',
    lastScannedUrl: null
  });

  // Model generation store
  Alpine.store('modelGen', {
    isLoading: false,
    currentModelUrl: '',
    error: '',

    showError(message) {
      // Display error in toast
      const toastContainer = document.getElementById('toast-container');
      if (toastContainer) {
        toastContainer.innerHTML = `<div class='alert alert-error'><span>${message}</span></div>`;
        setTimeout(() => {
          if (toastContainer.innerHTML.includes(message)) {
            toastContainer.innerHTML = '';
          }
        }, 5000);
      }
    },

    async generateFromText(prompt, options = {}) {
      if (!Alpine.store('wallet').isConnected) {
        this.showError('Please connect your wallet first');
        throw new Error('Please connect your wallet first');
      }

      this.isLoading = true;
      this.error = '';

      try {
        console.log('Sending request with prompt:', prompt);
        
        const response = await fetch('/api/generate-model', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt,
            walletAddress: Alpine.store('wallet').address,
            ...options
          })
        });

        const data = await response.json();
        console.log('API Response:', data);

        if (!response.ok) {
          throw new Error(data.error || data.message || 'Failed to generate model');
        }

        this.currentModelUrl = data.modelUrl;
        
        // Award reality coins for successful generation
        await Alpine.store('realityCoins').earnCoins(10, 'Generated 3D model from text');
        
        return data.modelUrl;
      } catch (error) {
        console.error('Generation error:', error);
        this.error = error.message;
        this.showError(error.message);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async generateFromImage(imageFile, options = {}) {
      if (!Alpine.store('wallet').isConnected) {
        this.showError('Please connect your wallet first');
        throw new Error('Please connect your wallet first');
      }

      this.isLoading = true;
      this.error = '';

      try {
        const formData = new FormData();
        formData.append('image', imageFile);
        formData.append('walletAddress', Alpine.store('wallet').address);
        formData.append('options', JSON.stringify(options));

        const response = await fetch('/api/generate-model-from-image', {
          method: 'POST',
          body: formData
        });

        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.error || data.message || 'Failed to generate model from image');
        }

        this.currentModelUrl = data.modelUrl;
        
        // Award reality coins for successful generation
        await Alpine.store('realityCoins').earnCoins(15, 'Generated 3D model from image');
        
        return data.modelUrl;
      } catch (error) {
        console.error('Image generation error:', error);
        this.error = error.message;
        this.showError(error.message);
        throw error;
      } finally {
        this.isLoading = false;
      }
    }
  });

  // Global Alpine functions
  Alpine.data('modelGenerator', () => ({
    activeTab: 'text',
    
    init() {
      // Try to auto-connect on page load
      Alpine.store('wallet').tryAutoConnect();

      // Listen for reality coins updates
      window.addEventListener('reality-coins-updated', (event) => {
        console.log('Reality coins balance updated:', event.detail.balance);
      });
    }
  }));

  // NFC handling functions
  window.startNfcScan = async function() {
    if ('NDEFReader' in window) {
      try {
        const ndef = new NDEFReader();
        Alpine.store('tap').nfcStatus = 'scanning';
        
        await ndef.scan();
        
        ndef.addEventListener("reading", ({ message, serialNumber }) => {
          for (const record of message.records) {
            if (record.recordType === "url") {
              const decoder = new TextDecoder();
              const url = decoder.decode(record.data);
              if (url.includes('cseas.fun/t/')) {
                Alpine.store('tap').lastScannedUrl = url;
              }
            }
          }
        });

      } catch (error) {
        console.error(error);
        Alpine.store('tap').nfcStatus = 'error';
      }
    } else {
      Alpine.store('tap').nfcStatus = 'error';
    }
  }

  window.stopNfcScan = function() {
    Alpine.store('tap').nfcStatus = 'idle';
  }
});

// Handle return from Phantom deeplink
window.addEventListener('load', () => {
  // Check if we're returning from a Phantom deeplink
  const url = new URL(window.location.href);
  if (url.searchParams.has('phantom_encryption_public_key')) {
    // Try to connect
    Alpine.store('wallet').connect();
  }
});

// Register custom elements if needed
if ('customElements' in window) {
  // Add any custom element definitions here
  // Example: customElements.define('model-viewer', ModelViewer);
}

// Add to your main.js or create a new bubbleBobber.js file
Alpine.data('bubbleBobber', () => ({
  isPlaying: false,
  score: 0,
  oxygen: 100,
  showStory: false,
  selectedSeavatar: null,
  gameLoop: null,
  canvas: null,
  ctx: null,
  pendant: {
      x: 0,
      y: 0,
      radius: 30,
      rotation: 0
  },
  bubbles: [],
  obstacles: [],
  seavatars: [
      { id: 1, name: 'Turtle', image: '/assets/images/turtle-avatar.png' },
      { id: 2, name: 'Dolphin', image: '/assets/images/dolphin-avatar.png' },
      { id: 3, name: 'Seahorse', image: '/assets/images/seahorse-avatar.png' }
  ],

  init() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext('2d');
      this.resizeCanvas();
      window.addEventListener('resize', () => this.resizeCanvas());
      this.setupControllers();
  },

  resizeCanvas() {
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
      this.pendant.x = this.canvas.width / 2;
      this.pendant.y = this.canvas.height / 2;
  },

  startGame() {
      if (!this.selectedSeavatar) {
          this.$store.toast.error('Please select a Seavatar first!');
          return;
      }
      
      this.isPlaying = true;
      this.score = 0;
      this.oxygen = 100;
      this.bubbles = [];
      this.obstacles = [];
      this.gameLoop = requestAnimationFrame(() => this.update());
  },

  async endGame() {
      this.isPlaying = false;
      cancelAnimationFrame(this.gameLoop);
      
      // Award CSEAS tokens based on score
      if (this.$store.wallet.isConnected) {
          try {
              await this.$store.realityCoins.earnCoins(
                  Math.floor(this.score / 10),
                  'Bubble Bobber game rewards'
              );
          } catch (error) {
              console.error('Failed to award CSEAS:', error);
          }
      }
  },

  update() {
      if (!this.isPlaying) return;

      // Clear canvas
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      // Update oxygen
      this.oxygen = Math.max(0, this.oxygen - 0.1);
      if (this.oxygen <= 0) {
          this.endGame();
          return;
      }

      // Update game elements
      this.updateBubbles();
      this.updateObstacles();
      this.checkCollisions();
      this.draw();

      this.gameLoop = requestAnimationFrame(() => this.update());
  },

  // Add remaining game logic methods here...
  selectSeavatar(id) {
      this.selectedSeavatar = id;
  },

  setupControllers() {
      // Mouse/touch controls
      const isMobile = 'ontouchstart' in window;
      
      if (isMobile) {
          window.addEventListener('deviceorientation', (e) => {
              if (!this.isPlaying) return;
              // Implement tilt controls
          });
      } else {
          this.canvas.addEventListener('mousemove', (e) => {
              if (!this.isPlaying) return;
              const rect = this.canvas.getBoundingClientRect();
              this.pendant.x = e.clientX - rect.left;
              this.pendant.y = e.clientY - rect.top;
          });
      }
  }
}));

Alpine.data('waveLens', () => ({
  waveHeight: 0,
  waterTemp: 0,
  airTemp: 0,
  windSpeed: 0,
  selectedStation: '41114',
  availableStations: [
    { id: '41114', name: 'Ft Pierce, FL', location: '27.547°N, 79.588°W' },
    { id: '41009', name: 'Canaveral, FL', location: '28.507°N, 80.186°W' },
    { id: '41010', name: 'St. Augustine, FL', location: '30.040°N, 80.553°W' },
    { id: '41013', name: 'Daytona Beach, FL', location: '29.231°N, 80.656°W' }
  ],
  
  init() {
    this.fetchBuoyData();
    // Set up periodic data updates every 5 minutes
    setInterval(() => this.fetchBuoyData(), 300000);
  },
  
  async fetchBuoyData() {
    try {
      const response = await fetch(`/api/buoy-data?station=${this.selectedStation}`);
      const data = await response.json();
      
      this.waveHeight = parseFloat(data.waveHeight);
      this.waterTemp = parseFloat(data.waterTemp);
      this.airTemp = parseFloat(data.airTemp);
      this.windSpeed = parseFloat(data.windSpeed);
      
      // Update the 3D visualization with new data
      this.updateVisualization();
    } catch (error) {
      console.error('Error fetching buoy data:', error);
    }
  },
  
  updateVisualization() {
    // This will be called by the Three.js visualization to update based on real data
    if (window.waveLensVisualization) {
      window.waveLensVisualization.updateWaveHeight(this.waveHeight);
      window.waveLensVisualization.updateWindSpeed(this.windSpeed);
    }
  },
  
  changeStation(stationId) {
    this.selectedStation = stationId;
    this.fetchBuoyData();
  }
}));
